import { Component, OnInit, OnDestroy } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { UnionService } from 'src/app/services/union.service';
import { RoleService } from 'src/app/services/role.service';

interface Union {
  id: number;
  union_name: string;
  upozilla_name: string;
  zilla_name: string;
  subdomain: string;
  status: 'active' | 'suspended' | 'maintenance';
  suspended_at?: string;
  suspension_reason?: string;
  suspended_by?: number;
  suspended_by_user?: {
    id: number;
    username: string;
    name?: string;
  };
  reactivated_at?: string;
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

interface StatusChangeData {
  unionId: number;
  newStatus: string;
  reason?: string;
  notes?: string;
}

@Component({
  selector: 'app-unions-list',
  templateUrl: './unions-list.component.html',
  styleUrls: ['./unions-list.component.scss']
})
export class UnionsListComponent implements OnInit, OnDestroy {
  subscriptions: Subscription[] = [];
  unions: Union[] = [];
  isLoading: boolean = false;
  
  // Math object for template use
  Math = Math;
  
  // Pagination
  currentPage: number = 1;
  totalPages: number = 1;
  totalCount: number = 0;
  pageSize: number | string = 10;
  pageSizeOptions: (number | string)[] = [5, 10, 20, 50];
  
  // Filters
  searchTerm: string = '';
  statusFilter: string = '';
  sortBy: string = 'createdAt';
  sortOrder: string = 'DESC';
  
  // Status change modal
  showStatusModal: boolean = false;
  selectedUnion: Union | null = null;
  statusChangeForm!: FormGroup;
  isSubmittingStatus: boolean = false;

  // Status options
  statusOptions = [
    { value: 'active', label: 'সক্রিয় করুন', icon: 'fa-check-circle', class: 'success' },
    { value: 'suspended', label: 'স্থগিত করুন', icon: 'fa-ban', class: 'danger' },
    { value: 'maintenance', label: 'রক্ষণাবেক্ষণ মোড', icon: 'fa-tools', class: 'warning' }
  ];

  constructor(
    private unionService: UnionService,
    private roleService: RoleService,
    private toastr: ToastrService
  ) {
    this.initializeStatusChangeForm();
  }

  ngOnInit(): void {
    if (!this.canManageUnions()) {
      this.toastr.error('আপনার এই পেজে প্রবেশের অনুমতি নেই', 'অনুমতি নেই');
      return;
    }

    this.loadUnions();
    this.setupSearchDebounce();
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  canManageUnions(): boolean {
    return this.roleService.isSuperAdmin();
  }

  getStatusFilterText(): string {
    const statusMap: { [key: string]: string } = {
      'active': 'সক্রিয়',
      'suspended': 'স্থগিত',
      'maintenance': 'রক্ষণাবেক্ষণ'
    };
    return statusMap[this.statusFilter] || '';
  }

  getStatusBadgeClass(union: Union): string {
    const status = union.status || 'active';
    switch (status) {
      case 'active':
        return 'badge-success';
      case 'suspended':
        return 'badge-danger';
      case 'maintenance':
        return 'badge-warning';
      default:
        return 'badge-secondary';
    }
  }



  initializeStatusChangeForm(): void {
    this.statusChangeForm = new FormGroup({
      newStatus: new FormControl('', [Validators.required]),
      reason: new FormControl(''),
      notes: new FormControl('')
    });
  }

  setupSearchDebounce(): void {
    // Create a FormControl for search to enable debouncing
    const searchControl = new FormControl(this.searchTerm);
    
    const subscription = searchControl.valueChanges.pipe(
      debounceTime(500),
      distinctUntilChanged()
    ).subscribe(value => {
      this.searchTerm = value || '';
      this.currentPage = 1;
      this.loadUnions();
    });

    this.subscriptions.push(subscription);
  }

  loadUnions(): void {
    this.isLoading = true;
    
    const params = {
      page: this.currentPage,
      limit: this.pageSize,
      search: this.searchTerm,
      status: this.statusFilter,
      sortBy: this.sortBy,
      sortOrder: this.sortOrder
    };

    const subscription = this.unionService.getAllUnions(params).subscribe({
      next: (response) => {
        this.unions = response.unions || [];
        this.totalPages = response.pagination?.totalPages || 1;
        this.totalCount = response.pagination?.totalCount || 0;
        this.isLoading = false;
      },
      error: (error) => {
        console.error('Error loading unions:', error);
        this.toastr.error('ইউনিয়ন তালিকা লোড করতে ব্যর্থ', 'ত্রুটি');
        this.isLoading = false;
      }
    });

    this.subscriptions.push(subscription);
  }

  onSearchChange(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.searchTerm = target.value;
    this.currentPage = 1;
    this.loadUnions();
  }

  onStatusFilterChange(): void {
    this.currentPage = 1;
    this.loadUnions();
  }

  onPageSizeChange(): void {
    this.currentPage = 1;
    this.loadUnions();
  }

  onPageChange(page: number): void {
    if (page >= 1 && page <= this.totalPages) {
      this.currentPage = page;
      this.loadUnions();
    }
  }

  onSort(field: string): void {
    if (this.sortBy === field) {
      this.sortOrder = this.sortOrder === 'ASC' ? 'DESC' : 'ASC';
    } else {
      this.sortBy = field;
      this.sortOrder = 'ASC';
    }
    this.loadUnions();
  }

  openStatusModal(union: Union): void {
    this.selectedUnion = union;
    this.statusChangeForm.patchValue({
      newStatus: union.status,
      reason: '',
      notes: ''
    });
    this.showStatusModal = true;
  }

  closeStatusModal(): void {
    this.showStatusModal = false;
    this.selectedUnion = null;
    this.statusChangeForm.reset();
  }

  submitStatusChange(): void {
    if (!this.selectedUnion || !this.statusChangeForm.valid) {
      return;
    }

    const formValue = this.statusChangeForm.value;
    
    if (formValue.newStatus === this.selectedUnion.status) {
      this.toastr.info('স্ট্যাটাস ইতিমধ্যে একই রয়েছে', 'তথ্য');
      this.closeStatusModal();
      return;
    }

    this.isSubmittingStatus = true;

    const statusChangeData: StatusChangeData = {
      unionId: this.selectedUnion.id,
      newStatus: formValue.newStatus,
      reason: formValue.reason || undefined,
      notes: formValue.notes || undefined
    };

    const subscription = this.unionService.toggleUnionStatus(statusChangeData).subscribe({
      next: (response) => {
        this.toastr.success(response.message || 'স্ট্যাটাস সফলভাবে পরিবর্তন করা হয়েছে', 'সফল');
        this.isSubmittingStatus = false;
        this.closeStatusModal();
        this.loadUnions(); // Reload the list
      },
      error: (error) => {
        console.error('Error changing union status:', error);
        this.toastr.error(error.error?.message || 'স্ট্যাটাস পরিবর্তন করতে ব্যর্থ', 'ত্রুটি');
        this.isSubmittingStatus = false;
      }
    });

    this.subscriptions.push(subscription);
  }

  getStatusText(status: string): string {
    switch (status) {
      case 'active': return 'সক্রিয়';
      case 'suspended': return 'স্থগিত';
      case 'maintenance': return 'রক্ষণাবেক্ষণ';
      default: return status;
    }
  }

  getStatusClass(status: string): string {
    switch (status) {
      case 'active': return 'status-active';
      case 'suspended': return 'status-suspended';
      case 'maintenance': return 'status-maintenance';
      default: return 'status-unknown';
    }
  }

  getStatusIcon(status: string): string {
    switch (status) {
      case 'active': return 'fa-check-circle';
      case 'suspended': return 'fa-ban';
      case 'maintenance': return 'fa-tools';
      default: return 'fa-question-circle';
    }
  }

  formatDate(dateString: string): string {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('bn-BD', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  getPaginationNumbers(): number[] {
    const delta = 2;
    const range = [];
    const rangeWithDots = [];

    for (let i = Math.max(2, this.currentPage - delta);
         i <= Math.min(this.totalPages - 1, this.currentPage + delta);
         i++) {
      range.push(i);
    }

    if (this.currentPage - delta > 2) {
      rangeWithDots.push(1, -1);
    } else {
      rangeWithDots.push(1);
    }

    rangeWithDots.push(...range);

    if (this.currentPage + delta < this.totalPages - 1) {
      rangeWithDots.push(-1, this.totalPages);
    } else if (this.totalPages > 1) {
      rangeWithDots.push(this.totalPages);
    }

    return rangeWithDots;
  }

  refreshList(): void {
    this.loadUnions();
  }

  clearFilters(): void {
    this.searchTerm = '';
    this.statusFilter = '';
    this.currentPage = 1;
    this.loadUnions();
  }

  trackByUnionId(index: number, union: Union): number {
    return union.id;
  }
}